/* AUTO-SYNCED from PsyKit.io/components/bilateral/BallCanvas.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useRef, useEffect, useCallback } from "react";

interface BallCanvasProps {
  /** Tempo in true Hz. 1 cycle = left → right → left.
   *  Range: 0.5–2.5 Hz. Default 1.0 Hz. */
  hz: number;
  ballSize: number;
  ballColor: string;
  canvasBg: string;
  isRunning: boolean;
  isStopping: boolean;
  /** Fired every animation frame: normalized position (0–1), current Hz, elapsed seconds. */
  onPositionChange?: (xNorm: number, hz: number, elapsedSeconds: number) => void;
  /** Fired each time the ball reaches a travel edge (one half-cycle = one pass).
   *  `side` is the edge just reached — derived from the sign of sin(2π·hz·t) at
   *  the peak. Sinusoidal motion makes successive passes strictly alternate. */
  onPassComplete?: (side: "left" | "right") => void;
  onStopComplete?: () => void;
  fillContainer?: boolean;
  /** Show a debug readout overlay with Hz, expected cycle, measured cycle. */
  showDebug?: boolean;
}

function lighten(hex: string, amt: number): string {
  const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + amt);
  const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + amt);
  const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + amt);
  return `rgb(${r},${g},${b})`;
}

function darken(hex: string, amt: number): string {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amt);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amt);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amt);
  return `rgb(${r},${g},${b})`;
}

function isLightColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 > 140;
}

export default function BallCanvas({
  hz,
  ballSize,
  ballColor,
  canvasBg,
  isRunning,
  isStopping,
  onPositionChange,
  onPassComplete,
  onStopComplete,
  fillContainer = false,
  showDebug = false,
}: BallCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number | null>(null);
  const debugRef = useRef<HTMLDivElement>(null);

  // Current ball X in canvas pixels (mutable, updated every frame)
  const ballXRef = useRef(0);

  // Stop transition state
  const stopRef = useRef({ startX: 0, progress: 0 });

  // Timing state for sinusoidal motion (all mutable, never trigger re-renders)
  const timingRef = useRef({
    startTime: 0,         // performance.now() when animation started
    prevElapsed: 0,       // elapsed (s) at previous frame — used for peak detection
    lastPeakTime: 0,      // performance.now() at the last edge crossing
    lastFullCycleTime: 0, // performance.now() at the start of the last full cycle
    measuredCycle: 0,     // measured full-cycle duration in seconds
    halfCycleCount: 0,    // counts half-cycles to detect full cycles
  });

  // Sync props into ref so the RAF loop always reads the latest values
  const propsRef = useRef({ hz, ballSize, ballColor, canvasBg, isRunning, isStopping });
  propsRef.current = { hz, ballSize, ballColor, canvasBg, isRunning, isStopping };

  const callbacksRef = useRef({ onPositionChange, onPassComplete, onStopComplete });
  callbacksRef.current = { onPositionChange, onPassComplete, onStopComplete };

  // ── Draw function (reads from propsRef + ballXRef, no React deps) ──
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
    }

    const { ballColor: color, canvasBg: bg, ballSize: radius } = propsRef.current;
    const ballX = ballXRef.current;
    const ballY = h / 2;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Vignette
    const light = isLightColor(bg);
    const vig = ctx.createRadialGradient(w / 2, h / 2, h * 0.3, w / 2, h / 2, h * 0.85);
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(1, light ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.45)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, w, h);

    // Glow ring
    const glow = ctx.createRadialGradient(ballX, ballY, radius * 0.5, ballX, ballY, radius * 1.8);
    glow.addColorStop(0, color + "22");
    glow.addColorStop(1, "transparent");
    ctx.beginPath();
    ctx.arc(ballX, ballY, radius * 1.8, 0, Math.PI * 2);
    ctx.fillStyle = glow;
    ctx.fill();

    // Ball with 3D radial gradient
    const grad = ctx.createRadialGradient(
      ballX - radius * 0.3, ballY - radius * 0.3, radius * 0.1,
      ballX, ballY, radius
    );
    grad.addColorStop(0, lighten(color, 30));
    grad.addColorStop(0.6, color);
    grad.addColorStop(1, darken(color, 25));
    ctx.beginPath();
    ctx.arc(ballX, ballY, radius, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
  }, []);

  // ── Animation loop ──
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const w = canvas.clientWidth;
    const {
      hz: currentHz,
      isStopping: stopping,
      isRunning: running,
    } = propsRef.current;
    const timing = timingRef.current;
    const stop = stopRef.current;

    if (stopping) {
      // Ease ball to center — independent of Hz, always takes ~0.8s
      stop.progress = Math.min(1, stop.progress + 0.012);
      const ease = 1 - Math.pow(1 - stop.progress, 3);
      ballXRef.current = stop.startX + (w / 2 - stop.startX) * ease;

      if (stop.progress >= 1) {
        ballXRef.current = w / 2;
        callbacksRef.current.onStopComplete?.();
        draw();
        return; // Stop the loop
      }
    } else if (running) {
      // ── Sinusoidal bilateral motion ──
      // Formula: x = centerX + amplitude × sin(2π × Hz × t)
      //   • amplitude = 35% of canvas width → total travel = 70% of width
      //   • t = elapsed seconds since session start
      //   • 1 full cycle (period) = 1 / Hz seconds
      const now = performance.now();
      const elapsed = (now - timing.startTime) / 1000; // seconds

      let xNorm: number;

      if (w > 0) {
        const centerX = w / 2;
        // Amplitude: reach within 12px of each edge, accounting for ball radius
        const { ballSize: radius } = propsRef.current;
        const amplitude = centerX - radius - 12;
        ballXRef.current = centerX + amplitude * Math.sin(2 * Math.PI * currentHz * elapsed);

        // ── Peak (edge) detection via cosine sign change ──
        const prevCos = Math.cos(2 * Math.PI * currentHz * timing.prevElapsed);
        const currCos = Math.cos(2 * Math.PI * currentHz * elapsed);
        if (prevCos * currCos < 0) {
          timing.halfCycleCount++;
          const peakNow = performance.now();

          if (timing.halfCycleCount % 2 === 0 && timing.lastFullCycleTime > 0) {
            timing.measuredCycle = (peakNow - timing.lastFullCycleTime) / 1000;
            timing.lastFullCycleTime = peakNow;
          } else if (timing.lastFullCycleTime === 0) {
            timing.lastFullCycleTime = peakNow;
          }

          timing.lastPeakTime = peakNow;
          // sin > 0 at the peak ⇒ ball is at the +amplitude (right) edge.
          // sin only changes sign at center crossings, so reading it at this
          // post-peak frame gives the correct side without extra state.
          const side: "left" | "right" =
            Math.sin(2 * Math.PI * currentHz * elapsed) > 0 ? "right" : "left";
          callbacksRef.current.onPassComplete?.(side);
        }

        xNorm = ballXRef.current / w;

        // Update debug overlay via direct DOM (avoids React re-renders every frame)
        if (debugRef.current && showDebug) {
          const expected = (1 / currentHz).toFixed(3);
          const measured =
            timing.measuredCycle > 0 ? timing.measuredCycle.toFixed(3) : "—";
          debugRef.current.textContent =
            `${currentHz.toFixed(1)} Hz  ·  Expected: ${expected}s  ·  Measured: ${measured}s`;
        }
      } else {
        // Canvas is hidden (display:none → clientWidth = 0).
        // Compute normalised position from time so audio keeps panning.
        xNorm = 0.5 + 0.5 * Math.sin(2 * Math.PI * currentHz * elapsed);
      }

      timing.prevElapsed = elapsed;

      // Emit position + Hz + elapsed for audio panning and Supabase broadcast
      callbacksRef.current.onPositionChange?.(xNorm, currentHz, elapsed);
    }

    draw();

    if (running || stopping) {
      animRef.current = requestAnimationFrame(animate);
    }
  }, [draw, showDebug]);

  // ── Handle resize: recentre ball when not running ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleResize = () => {
      if (!propsRef.current.isRunning && !propsRef.current.isStopping) {
        ballXRef.current = canvas.clientWidth / 2;
      }
      draw();
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [draw]);

  // ── Start animation ──
  useEffect(() => {
    if (isRunning && !isStopping) {
      const canvas = canvasRef.current;
      // Reset timing for a fresh cycle starting at centre (sin(0) = 0)
      timingRef.current = {
        startTime: performance.now(),
        prevElapsed: 0,
        lastPeakTime: 0,
        lastFullCycleTime: 0,
        measuredCycle: 0,
        halfCycleCount: 0,
      };
      if (canvas) ballXRef.current = canvas.clientWidth / 2;
      animRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
        animRef.current = null;
      }
    };
  }, [isRunning, isStopping, animate]);

  // ── Stop transition ──
  useEffect(() => {
    if (isStopping) {
      stopRef.current = { startX: ballXRef.current, progress: 0 };
      animRef.current = requestAnimationFrame(animate);
    }
  }, [isStopping, animate]);

  // ── Redraw when visual props change while paused; also reset timing so
  //    the next Start begins with a clean half-cycle count. ──
  useEffect(() => {
    if (!isRunning && !isStopping) {
      timingRef.current = {
        startTime: 0,
        prevElapsed: 0,
        lastPeakTime: 0,
        lastFullCycleTime: 0,
        measuredCycle: 0,
        halfCycleCount: 0,
      };
      const canvas = canvasRef.current;
      if (canvas) ballXRef.current = canvas.clientWidth / 2;
      draw();
    }
  }, [ballColor, canvasBg, ballSize, isRunning, isStopping, draw]);

  return (
    <div
      className={
        fillContainer
          ? "relative w-full h-full overflow-hidden"
          : "relative w-full aspect-[2/1] rounded-[4px] overflow-hidden border border-[var(--color-navy-rim)]"
      }
    >
      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <canvas ref={canvasRef} className="block w-full h-full relative z-[1]" />

      {/* Debug overlay — updated via direct DOM ref, zero React re-renders */}
      {showDebug && (
        <div
          ref={debugRef}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
          style={{
            fontFamily: "var(--font-dm-mono, monospace)",
            fontSize: "10px",
            color: "rgba(200,168,76,0.7)",
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
          }}
        />
      )}
    </div>
  );
}
